// SellerLogin.jsx

import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import React, { useState, useEffect } from "react";
import api from "../api";
import { Eye, EyeOff } from "react-feather";
import { useGoogleLogin } from "@react-oauth/google";
import glogo from "../../feature/glogo.png";

function SellerLogin() {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const [formError, setFormError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);

    const [showPassword, setShowPassword] = useState(false);

    const [loginFormData, setLoginFormData] = useState({
        email: "",
        username: "",
        password: "",
    });

    const location = useLocation();

    const inputHandler = (event) => {
        setLoginFormData({
            ...loginFormData,
            [event.target.name]: event.target.value,
        });
    };

    // Prefill shopname
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const shopnameFromUrl = params.get("shopname");

        if (shopnameFromUrl && !loginFormData.username) {
            setLoginFormData((prev) => ({
                ...prev,
                username: shopnameFromUrl.toLowerCase(),
            }));
        }
    }, [location.search]);

    const submitHandler = async (event) => {
        event.preventDefault();

        localStorage.clear();

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (
            loginFormData.email !== "" &&
            !emailPattern.test(loginFormData.email)
        ) {
            setEmailError(true);
            return;
        } else {
            setEmailError(false);
        }

        if (loginFormData.password.length < 7) {
            setPasswordError(true);
            return;
        } else {
            setPasswordError(false);
        }

        setLoading(true);
        setErrorMsg("");

        const formData = {
            email: loginFormData.email.toLowerCase(),
            password: loginFormData.password,
        };

        try {
            const response = await axios.post(
                baseUrl + "/vendor/login/",
                formData
            );

            if (response.data.bool) {
                const {
                    access_token,
                    refresh_token,
                    user,
                    shop,
                    id,
                } = response.data;

                localStorage.setItem("access_token", access_token);
                localStorage.setItem("refresh_token", refresh_token);
                localStorage.setItem("vendor_username", user);
                localStorage.setItem("vendor_shopname", shop);
                localStorage.setItem("vendor_id", id);
                localStorage.setItem("vendor_login", true);

                window.location.href = "/seller/dashboard";
            } else {
                setFormError(true);
                setLoading(false);
                setErrorMsg(response.data.message || "Login failed");
            }
        } catch (error) {
            setFormError(true);
            setLoading(false);
            setErrorMsg(
                "Oops! Confirm your credentials and network connection, then try again."
            );
        }
    };

    // GOOGLE LOGIN
    const googleLogin = useGoogleLogin({
        flow: "implicit",

        onSuccess: async (tokenResponse) => {
            try {
                const response = await axios.post(
                    baseUrl + "/vendor/google-auth/",
                    {
                        token: tokenResponse.access_token
                    }
                );

                if (response.data.bool) {
                    const {
                        access_token,
                        refresh_token,
                        user,
                        shop,
                        id
                    } = response.data;

                    localStorage.setItem("access_token", access_token);
                    localStorage.setItem("refresh_token", refresh_token);
                    localStorage.setItem("vendor_username", user);
                    localStorage.setItem("vendor_shopname", shop);
                    localStorage.setItem("vendor_id", id);
                    localStorage.setItem("vendor_login", true);

                    window.location.href = "/seller/dashboard";
                }
            } catch {
                setFormError(true);
                setErrorMsg("Google login failed.");
            }
        },

        onError: () => {
            setFormError(true);
            setErrorMsg("Google login failed.");
        }
    });

    // Validate token
    useEffect(() => {
        const token = localStorage.getItem("access_token");

        if (!token && location.pathname !== "/seller/login") {
            window.location.href = "/seller/login";
            return;
        }

        if (token) {
            api.get(baseUrl + "/vendor/validate-token/", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((response) => {
                    if (!response.data.valid) {
                        localStorage.clear();
                        window.location.href =
                            "/seller/login";
                    }
                })
                .catch(() => {
                    localStorage.clear();
                    window.location.href =
                        "/seller/login";
                });
        }
    }, []);

    return (
        <>
            <Helmet>
                <title>Log in</title>
                <meta
                    name="description"
                    content="Log in"
                />
            </Helmet>

            <section className="container mtopcon normal-mt-sctn">
                <div className="row">
                    <div className="col-12 col-lg-4 reglogincon">
                        <div className="card shadow-none">
                            <div className="card-body">
                                <h2 className="mb-3 text-center">
                                    <b>Welcome back</b>
                                </h2>

                                {formError && (
                                    <p className="text-danger text-center">
                                        {errorMsg}
                                    </p>
                                )}

                                <div className="mb-3">
                                    <label className="form-label mb-0 font9 grey-text">
                                        Email
                                    </label>

                                    <input
                                        type="email"
                                        name="email"
                                        value={
                                            loginFormData.email
                                        }
                                        onChange={inputHandler}
                                        className="form-control"
                                    />

                                    {emailError && (
                                        <small className="text-danger font8">
                                            Kindly enter a valid
                                            email.
                                        </small>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label mb-0 font9 grey-text">
                                        Password
                                    </label>

                                    <div className="input-group">
                                        <input
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            name="password"
                                            onChange={
                                                inputHandler
                                            }
                                            value={
                                                loginFormData.password
                                            }
                                            className="form-control font9 br-0"
                                        />

                                        <button
                                            type="button"
                                            className="btn btn-outline-grey bl-0"
                                            onClick={() =>
                                                setShowPassword(
                                                    !showPassword
                                                )
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff
                                                    size={18}
                                                />
                                            ) : (
                                                <Eye size={18} />
                                            )}
                                        </button>
                                    </div>

                                    {passwordError && (
                                        <small className="text-danger font8">
                                            Kindly enter a valid
                                            password.
                                        </small>
                                    )}
                                </div>

                                <div className="mb-3 font9 text-right">
                                    <p>
                                        <Link
                                            to="/seller/reset-link"
                                            className="grey-text"
                                        >
                                            Forgot Password?
                                        </Link>
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={submitHandler}
                                    className="col-12 btn btn-dark big-btn"
                                >
                                    {loading
                                        ? "Loading..."
                                        : "Continue"}
                                </button>

                                {/* GOOGLE */}

                                <div className="google-auth-wrapper mt-4">
                                    <div className="auth-divider">
                                        <span>or</span>
                                    </div>

                                    <button
                                        type="button"
                                        className="google-auth-btn"
                                        onClick={() =>
                                            googleLogin()
                                        }
                                    >
                                        <img
                                            src={glogo}
                                            alt="Google"
                                            className="google-real-icon"
                                        />

                                        <span>
                                            Continue with Google
                                        </span>
                                    </button>
                                </div>

                                <div className="mb-3 f85 mt-4 grey-text text-center">
                                    <p>
                                        Don't have an account?{" "}
                                        <Link
                                            to="/seller/register"
                                            className="grey-text"
                                        >
                                            <b>Sign Up</b>
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default SellerLogin;